// v0.8.9: shared quest form. Extracted from QuestNewPage so the
// same fields / validation power both create and edit. The form
// owns its local state, initialised from `initial`; callers pass
// a submit handler that receives the API-shaped body.
//
// Create (QuestNewPage) and edit (QuestDetailPage) differ only by
// the team picker (create needs one; edit leaves the quest on its
// team) and the submit label. Everything else — title, description,
// event type, splits (competency rewards), mode, max completions,
// repeatable, review mode, due date, assignees — is identical.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';

interface Team { id: string; name: string; slug: string }
interface User { id: string; handle: string; display_name: string; email: string }
interface Competency { id: string; name: string; slug: string; dimension: 'technical' | 'behavioral' }

export interface SplitDraft {
  // Local form state for one row of the splits editor.
  // `key` is a stable React id (separate from
  // competency_id, which is empty until the admin picks
  // a competency). xp + coins are the reward shape;
  // coins is optional (default 0) so pre-M5.15 quests
  // continue to work without a backfill.
  key: string;
  competency_id: string;
  xp: number;
  coins: number;
}

export function newSplitDraft(): SplitDraft {
  return { key: `s_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`, competency_id: '', xp: 5, coins: 0 };
}

export interface QuestFormValue {
  title: string;
  description: string;
  event_type: string;
  splits: SplitDraft[];
  mode: 'open' | 'assigned';
  max_completions: number;
  review_mode: 'auto' | 'review';
  repeatable: boolean;
  // Local <input type="date"> value (YYYY-MM-DD), '' when unset.
  due_at: string;
  assign_to: string[];
}

export interface QuestFormBody {
  title: string;
  description: string;
  event_type: string;
  splits: Array<{ competency_id: string; xp: number; coins: number }>;
  mode: 'open' | 'assigned';
  max_completions: number;
  review_mode: 'auto' | 'review';
  repeatable: boolean;
  due_at: string | null;
  assign_to: string[];
}

interface QuestFormProps {
  initial: QuestFormValue;
  // Team picker is create-only. When showTeam is true the caller
  // also controls teamId via onTeamChange.
  showTeam?: boolean;
  teamId?: string;
  onTeamChange?: (id: string) => void;
  teams?: Team[];
  submitLabel: string;
  submitting: boolean;
  error: string | null;
  onSubmit: (body: QuestFormBody) => void;
  cancelHref: string;
  // Disable the submit button in addition to the built-in
  // validation (e.g. create needs a team picked).
  submitDisabled?: boolean;
}

// Convert an ISO timestamp (or null) to a local <input
// type="date"> value (YYYY-MM-DD). Empty string when unset.
export function isoToDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Convert an <input type="date"> value back to an ISO
// timestamp. Empty input → null (api clears due_at).
function dateInputToIso(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export function QuestForm({
  initial,
  showTeam = false,
  teamId = '',
  onTeamChange,
  teams,
  submitLabel,
  submitting,
  error,
  onSubmit,
  cancelHref,
  submitDisabled = false,
}: QuestFormProps) {
  const [form, setForm] = useState<QuestFormValue>(initial);
  const competencies = useQuery({
    queryKey: ['competencies'],
    queryFn: () => api<{ competencies: Competency[] }>('/api/competencies'),
  });
  // Only fetch users when the assignee editor is reachable
  // (assigned mode). Keeps the create-default (open mode)
  // from an unnecessary request.
  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => api<{ users: User[] }>('/api/users'),
    enabled: form.mode === 'assigned',
  });

  // M5.15: validate the splits editor locally so the
  // Save button reflects the api's rules.
  const splitsValidation = useMemo(() => {
    if (form.splits.length === 0) return { ok: false, error: 'Add at least one competency.' };
    const ids = new Set<string>();
    for (let i = 0; i < form.splits.length; i++) {
      const s = form.splits[i]!;
      if (!s.competency_id) return { ok: false, error: `Row ${i + 1}: pick a competency.` };
      if (!Number.isFinite(s.xp) || s.xp < 0 || s.xp > 10_000) {
        return { ok: false, error: `Row ${i + 1}: XP must be 0–10000.` };
      }
      if (!Number.isFinite(s.coins) || s.coins < 0 || s.coins > 1000 || !Number.isInteger(s.coins)) {
        return { ok: false, error: `Row ${i + 1}: coins must be an integer 0–1000.` };
      }
      if (ids.has(s.competency_id)) {
        return { ok: false, error: `Row ${i + 1}: duplicate competency.` };
      }
      ids.add(s.competency_id);
    }
    return { ok: true as const };
  }, [form.splits]);

  const totalXp = form.splits.reduce((sum, s) => sum + (Number(s.xp) || 0), 0);
  const totalCoins = form.splits.reduce((sum, s) => sum + (Number(s.coins) || 0), 0);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          title: form.title,
          description: form.description,
          event_type: form.event_type,
          splits: form.splits.map((s) => ({ competency_id: s.competency_id, xp: s.xp, coins: s.coins })),
          mode: form.mode,
          max_completions: form.max_completions,
          review_mode: form.review_mode,
          repeatable: form.repeatable,
          due_at: dateInputToIso(form.due_at),
          assign_to: form.assign_to,
        });
      }}
      className="card grid gap-4 sm:grid-cols-2"
    >
      <div className="space-y-1 sm:col-span-2">
        <label className="label" htmlFor="title">Title</label>
        <input
          id="title"
          className="input"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          required
          maxLength={200}
          placeholder="Review the Q3 customer feedback survey"
        />
      </div>
      <div className="space-y-1 sm:col-span-2">
        <label className="label" htmlFor="desc">Description</label>
        <textarea
          id="desc"
          className="input min-h-[5rem]"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          maxLength={5000}
          placeholder="What needs to happen, how to mark it done, any links."
        />
      </div>
      {showTeam ? (
        <div className="space-y-1">
          <label className="label" htmlFor="team">Team</label>
          <select
            id="team"
            className="input"
            value={teamId}
            onChange={(e) => onTeamChange?.(e.target.value)}
            required
          >
            <option value="">Select…</option>
            {(teams ?? []).map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
      ) : null}
      <div className="space-y-1">
        <label className="label" htmlFor="event">Event type</label>
        <input
          id="event"
          className="input font-mono"
          value={form.event_type}
          onChange={(e) => setForm({ ...form, event_type: e.target.value })}
          placeholder="quest.completed"
        />
        <p className="text-xs text-ink-dim">Stays as <code>quest.completed</code> unless you have a custom rule.</p>
      </div>

      {/* M5.15: multi-competency rewards editor. Each row is
          {competency, xp, coins}; one or more rows compose the
          quest's `splits` array. */}
      <div className="space-y-2 sm:col-span-2">
        <div className="flex items-baseline justify-between">
          <label className="label">Competency rewards</label>
          <span className="text-xs text-ink-dim">
            Total: <span className="font-mono text-ink">{totalXp} XP</span>
            {totalCoins > 0 ? (
              <>
                {' · '}
                <span className="font-mono text-ink">{totalCoins} 🪙</span>
              </>
            ) : null}
          </span>
        </div>
        <div className="space-y-2">
          {form.splits.map((s, i) => {
            const usedElsewhere = form.splits.some((other, j) => j !== i && other.competency_id === s.competency_id);
            return (
              <div key={s.key} className="flex items-center gap-2">
                <select
                  className="input flex-1"
                  value={s.competency_id}
                  onChange={(e) => {
                    const next = [...form.splits];
                    next[i] = { ...next[i]!, competency_id: e.target.value };
                    setForm({ ...form, splits: next });
                  }}
                  required
                >
                  <option value="">— competency —</option>
                  {(competencies.data?.competencies ?? []).map((c) => (
                    <option key={c.id} value={c.id} disabled={usedElsewhere && form.splits.find((o) => o.competency_id === c.id) !== form.splits[i]}>
                      {c.name} ({c.dimension})
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  min="0"
                  max="10000"
                  className="input w-20"
                  value={s.xp}
                  onChange={(e) => {
                    const next = [...form.splits];
                    next[i] = { ...next[i]!, xp: Number(e.target.value) };
                    setForm({ ...form, splits: next });
                  }}
                  aria-label="XP for this competency"
                />
                <span className="text-xs text-ink-dim">XP</span>
                <input
                  type="number"
                  min="0"
                  max="1000"
                  step="1"
                  className="input w-20"
                  value={s.coins}
                  onChange={(e) => {
                    const next = [...form.splits];
                    next[i] = { ...next[i]!, coins: Number(e.target.value) };
                    setForm({ ...form, splits: next });
                  }}
                  aria-label="Coins for this competency"
                  title="Coins awarded when this split is credited (integer 0-1000). Optional — leave 0 for no coin reward."
                />
                <span className="text-xs text-ink-dim">🪙</span>
                <button
                  type="button"
                  className="btn px-2 py-1 text-xs"
                  onClick={() => setForm({ ...form, splits: form.splits.filter((_, j) => j !== i) })}
                  disabled={form.splits.length === 1}
                  title={form.splits.length === 1 ? 'At least one competency is required' : 'Remove this row'}
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
        <button
          type="button"
          className="btn text-xs"
          onClick={() => setForm({ ...form, splits: [...form.splits, newSplitDraft()] })}
        >
          + Add competency
        </button>
        {!splitsValidation.ok ? (
          <p className="text-xs text-accent-danger" role="alert">{splitsValidation.error}</p>
        ) : (
          <p className="text-xs text-ink-dim">
            Each row is one {`{competency_id, xp, coins}`} split. On completion, the player is credited XP per row in a single transaction (mirrors the scoring-rule splits pattern, ADR-0009). Coins credit to the coin ledger with <code>kind='earn', quest_id=…</code> per row.
          </p>
        )}
      </div>

      <div className="space-y-1">
        <label className="label" htmlFor="mode">Mode</label>
        <select id="mode" className="input" value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value as typeof form.mode })}>
          <option value="open">Open (first-come)</option>
          <option value="assigned">Assigned (specific users)</option>
        </select>
      </div>
      <div className="space-y-1">
        <label className="label" htmlFor="max">Max completions</label>
        <input id="max" type="number" min="1" className="input" value={form.max_completions} onChange={(e) => setForm({ ...form, max_completions: Number(e.target.value) })} />
      </div>
      <div className="space-y-1 sm:col-span-2">
        <label className="flex items-start gap-2 text-sm">
          <input
            id="repeatable"
            type="checkbox"
            className="mt-1 h-4 w-4"
            checked={form.repeatable}
            onChange={(e) => setForm({ ...form, repeatable: e.target.checked })}
            title="When on, the same user can claim+complete this quest multiple times. Each completion counts against the global Max completions cap."
          />
          <span>
            <span className="font-medium">Repeatable</span>
            <span className="ml-2 text-xs text-ink-dim">Same user can complete this multiple times</span>
          </span>
        </label>
        {form.repeatable && form.max_completions === 1 ? (
          <p className="text-xs text-accent-warning">
            Max completions is 1, so a single user can only complete this once. Bump Max completions if you want the same user to repeat.
          </p>
        ) : null}
      </div>
      <div className="space-y-1">
        <label className="label" htmlFor="review">Review mode</label>
        <select id="review" className="input" value={form.review_mode} onChange={(e) => setForm({ ...form, review_mode: e.target.value as typeof form.review_mode })}>
          <option value="auto">Auto (player marks done, XP fires immediately)</option>
          <option value="review">Review (admin must confirm)</option>
        </select>
      </div>
      <div className="space-y-1">
        <label className="label" htmlFor="due">Due date (optional)</label>
        <input id="due" type="date" className="input" value={form.due_at} onChange={(e) => setForm({ ...form, due_at: e.target.value })} />
      </div>
      {form.mode === 'assigned' ? (
        <div className="space-y-1 sm:col-span-2">
          <label className="label">Assign to</label>
          <div className="grid max-h-48 gap-1 overflow-y-auto rounded-md border border-white/10 bg-bg-elevated p-2 sm:grid-cols-2">
            {(users.data?.users ?? []).map((u) => {
              const checked = form.assign_to.includes(u.id);
              return (
                <label key={u.id} className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-bg-panel">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={checked}
                    onChange={(e) => {
                      const next = e.target.checked
                        ? [...form.assign_to, u.id]
                        : form.assign_to.filter((x) => x !== u.id);
                      setForm({ ...form, assign_to: next });
                    }}
                  />
                  {u.display_name} <span className="text-xs text-ink-dim">@{u.handle}</span>
                </label>
              );
            })}
          </div>
          <p className="text-xs text-ink-dim">
            {form.assign_to.length} selected. Unchecking a player who hasn't claimed yet removes their access; anyone mid-flight or already done keeps their progress.
          </p>
        </div>
      ) : null}
      {error ? <p className="text-sm text-accent-danger sm:col-span-2" role="alert">{error}</p> : null}
      <div className="flex justify-end gap-2 sm:col-span-2">
        <Link to={cancelHref} className="btn">Cancel</Link>
        <button
          type="submit"
          className="btn-primary"
          disabled={submitting || submitDisabled || !form.title || !splitsValidation.ok}
        >
          {submitting ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
